import { useCallback, useEffect, useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { toast } from "react-hot-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable } from "@/components/ui/data-table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ConfirmationModal } from "@/components/ui/confirmation-modal";
import { formatDate } from "@/utils/formatters";
import { Pin, Play, RefreshCw, FlaskConical, AlertTriangle } from "lucide-react";
import {
  brandRankingAdminService,
  type RankingConfig,
  type RankingRun,
  type RankingStatus,
  type ScoringMode,
  type ScoringWindow,
} from "@/services/admin/brandRanking.admin.service";

const scoringModes: { value: ScoringMode; label: string; hint: string }[] = [
  { value: "ORDINAL", label: "Ordinal (positions)", hint: "Average of each metric's position — the original behaviour." },
  { value: "NORMALIZED", label: "Normalized (magnitude)", hint: "Each metric scored as value ÷ max, so a $1M brand and a $10 brand are far apart." },
];

const scoringWindows: { value: ScoringWindow; label: string }[] = [
  { value: "LIFETIME", label: "Lifetime" },
  { value: "LAST_30_DAYS", label: "Last 30 days" },
  { value: "LAST_90_DAYS", label: "Last 90 days" },
];

const weightFields: { key: keyof RankingConfig; label: string }[] = [
  { key: "revenueWeight", label: "Revenue" },
  { key: "commissionWeight", label: "Commissions" },
  { key: "ordersWeight", label: "Affiliate orders" },
  { key: "affiliatesWeight", label: "Affiliates" },
];

const toggleFields: { key: keyof RankingConfig; label: string; hint: string }[] = [
  { key: "requireActiveSession", label: "Require installed app", hint: "Only brands whose Shopify session is ACTIVE take part." },
  { key: "requireOnboardingCompleted", label: "Require completed onboarding", hint: "Half-onboarded brands keep whatever rank they have." },
  { key: "excludeTestAccounts", label: "Exclude test accounts", hint: "Test brands are left where they are." },
  { key: "skipMetricSyncForPinned", label: "Skip revenue sync for pinned brands", hint: "Fewer Shopify calls; unpinned brands are then ranked only against each other." },
];

function describeSchedule(schedule: RankingStatus["schedule"]) {
  if (!schedule.enabled) return "Disabled by BRAND_RANKING_ENABLED";
  if (schedule.cron) return `Cron "${schedule.cron}"${schedule.timezone ? ` (${schedule.timezone})` : ""}`;
  const hours = schedule.everyMs / 3_600_000;
  return hours >= 1 ? `Every ${Number(hours.toFixed(1))}h` : `Every ${Math.round(schedule.everyMs / 60_000)}m`;
}

function RunSummary({ run }: { run: RankingRun }) {
  return (
    <span className="text-sm text-muted-foreground">
      {run.trigger === "DRY_RUN" ? "Dry run" : run.trigger === "MANUAL" ? "Manual" : "Scheduled"} ·{" "}
      {run.status === "RUNNING" ? "running" : run.status.toLowerCase()} · {run.changedCount} moved ·{" "}
      {run.syncedCount} synced{run.syncFailedCount > 0 ? ` · ${run.syncFailedCount} sync failures` : ""}
    </span>
  );
}

export default function BrandRanking() {
  const [status, setStatus] = useState<RankingStatus | null>(null);
  const [config, setConfig] = useState<RankingConfig | null>(null);
  const [draft, setDraft] = useState<RankingConfig | null>(null);
  const [configMeta, setConfigMeta] = useState<{ updatedAt: string | null }>({ updatedAt: null });
  const [runs, setRuns] = useState<RankingRun[]>([]);
  const [selectedRun, setSelectedRun] = useState<RankingRun | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState<"run" | "dry" | "pins" | null>(null);
  const [confirmRun, setConfirmRun] = useState(false);

  const load = useCallback(async () => {
    try {
      const [statusData, configData, runData] = await Promise.all([
        brandRankingAdminService.getStatus(),
        brandRankingAdminService.getConfig(),
        brandRankingAdminService.listRuns(25),
      ]);
      setStatus(statusData);
      setConfig(configData.config);
      setDraft((current) => current ?? configData.config);
      setConfigMeta({ updatedAt: configData.updatedAt });
      setRuns(runData);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to load ranking");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Poll while a run is in progress so the status card catches up on its own.
  useEffect(() => {
    if (!status?.activeRun) return;
    const timer = setInterval(() => void load(), 5000);
    return () => clearInterval(timer);
  }, [status?.activeRun, load]);

  const dirty = useMemo(
    () => !!config && !!draft && JSON.stringify(config) !== JSON.stringify(draft),
    [config, draft],
  );

  const setField = <K extends keyof RankingConfig>(key: K, value: RankingConfig[K]) =>
    setDraft((current) => (current ? { ...current, [key]: value } : current));

  const numberField = (key: keyof RankingConfig, value: string, integer = false) => {
    const parsed = integer ? parseInt(value, 10) : parseFloat(value);
    setField(key, (Number.isFinite(parsed) ? Math.max(0, parsed) : 0) as never);
  };

  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const response = await brandRankingAdminService.updateConfig(draft);
      setConfig(response.config);
      setDraft(response.config);
      setConfigMeta({ updatedAt: response.updatedAt });
      toast.success("Ranking configuration saved");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleRun = async () => {
    setConfirmRun(false);
    setBusy("run");
    try {
      const result = await brandRankingAdminService.triggerRun();
      toast.success(result.queued ? "Ranking run queued" : "A ranking run is already queued");
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to queue run");
    } finally {
      setBusy(null);
    }
  };

  const handleDryRun = async () => {
    setBusy("dry");
    try {
      const run = await brandRankingAdminService.dryRun();
      setSelectedRun(run);
      toast.success(`Dry run: ${run.changedCount} brand(s) would move`);
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Dry run failed");
    } finally {
      setBusy(null);
    }
  };

  const handleApplyPins = async () => {
    setBusy("pins");
    try {
      const run = await brandRankingAdminService.applyPins();
      setSelectedRun(run);
      toast.success(`Pins applied: ${run.changedCount} brand(s) moved`);
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to apply pins");
    } finally {
      setBusy(null);
    }
  };

  const runColumns: ColumnDef<RankingRun>[] = [
    {
      id: "startedAt",
      header: "Started",
      meta: { width: "w-40" },
      cell: ({ row }) => (
        <span className="text-sm text-foreground">{formatDate(row.original.startedAt)}</span>
      ),
    },
    {
      id: "trigger",
      header: "Trigger",
      meta: { width: "w-28" },
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.trigger === "DRY_RUN" ? "Dry run" : row.original.trigger === "MANUAL" ? "Manual" : "Scheduled"}
        </span>
      ),
    },
    {
      id: "status",
      header: "Status",
      meta: { width: "w-28" },
      cell: ({ row }) => {
        const s = row.original.status;
        const tone =
          s === "COMPLETED"
            ? "text-green-600 dark:text-green-400"
            : s === "FAILED"
              ? "text-red-600 dark:text-red-400"
              : "text-[#d7785b]";
        return <span className={`text-sm font-medium ${tone}`}>{s.toLowerCase()}</span>;
      },
    },
    {
      id: "eligible",
      header: "Eligible",
      meta: { width: "w-24" },
      cell: ({ row }) => <span className="text-sm">{row.original.eligibleCount}</span>,
    },
    {
      id: "pinned",
      header: "Pinned",
      meta: { width: "w-24" },
      cell: ({ row }) => <span className="text-sm">{row.original.pinnedCount}</span>,
    },
    {
      id: "moved",
      header: "Moved",
      meta: { width: "w-24" },
      cell: ({ row }) => (
        <span className={`text-sm font-medium ${row.original.changedCount > 0 ? "text-foreground" : "text-muted-foreground"}`}>
          {row.original.changedCount}
        </span>
      ),
    },
    {
      id: "sync",
      header: "Revenue sync",
      meta: { width: "w-40" },
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.syncedCount} ok
          {row.original.syncFailedCount > 0 && (
            <span className="text-red-600 dark:text-red-400"> · {row.original.syncFailedCount} failed</span>
          )}
          {row.original.shopifyOrdersFetched > 0 && ` · ${row.original.shopifyOrdersFetched.toLocaleString()} orders`}
        </span>
      ),
    },
    {
      id: "warnings",
      header: "",
      meta: { width: "w-12" },
      cell: ({ row }) =>
        (row.original.warnings?.length ?? 0) > 0 || row.original.error ? (
          <AlertTriangle className="w-4 h-4 text-amber-500" aria-label="Has warnings" />
        ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Schedule</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => void load()} aria-label="Refresh">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {loading || !status ? (
              <div className="h-24 animate-pulse rounded-lg bg-muted" />
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Cadence</p>
                  <p className="text-sm font-medium text-foreground mt-1">{describeSchedule(status.schedule)}</p>
                  {!status.schedule.registered && status.schedule.enabled && (
                    <p className="text-xs text-amber-600 mt-1">Not registered in Redis yet</p>
                  )}
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Next run</p>
                  <p className="text-sm font-medium text-foreground mt-1">
                    {status.schedule.nextRunAt ? formatDate(status.schedule.nextRunAt) : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Brands</p>
                  <p className="text-sm font-medium text-foreground mt-1">
                    {status.brands.unpinned} free · {status.brands.pinned} pinned
                  </p>
                  {status.brands.unpinned === 0 && (
                    <p className="text-xs text-muted-foreground mt-1">Every brand is pinned — runs move nobody.</p>
                  )}
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Last run</p>
                  {status.activeRun ? (
                    <p className="text-sm font-medium text-[#d7785b] mt-1">Running since {formatDate(status.activeRun.startedAt)}</p>
                  ) : status.lastRun ? (
                    <div className="mt-1">
                      <p className="text-sm font-medium text-foreground">{formatDate(status.lastRun.startedAt)}</p>
                      <RunSummary run={status.lastRun} />
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground mt-1">Never</p>
                  )}
                </div>
                {status.revenueNeverSynced > 0 && (
                  <p className="col-span-full text-xs text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {status.revenueNeverSynced} onboarded brand(s) have never had revenue synced; their revenue reads as 0 until the next full run.
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Actions</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            <Button
              className="bg-[#d7785b] hover:bg-[#c66a4f] text-white justify-start"
              disabled={busy !== null || !!status?.activeRun}
              onClick={() => setConfirmRun(true)}
            >
              <Play className="w-4 h-4 mr-2" />
              {busy === "run" ? "Queueing…" : "Run now (with Shopify sync)"}
            </Button>
            <Button variant="outline" className="justify-start" disabled={busy !== null} onClick={handleDryRun}>
              <FlaskConical className="w-4 h-4 mr-2" />
              {busy === "dry" ? "Computing…" : "Dry run on cached metrics"}
            </Button>
            <Button variant="outline" className="justify-start" disabled={busy !== null} onClick={handleApplyPins}>
              <Pin className="w-4 h-4 mr-2" />
              {busy === "pins" ? "Applying…" : "Apply pins now"}
            </Button>
            <p className="text-xs text-muted-foreground mt-1">
              Pin and unpin brands from the Brands page. "Apply pins" re-places everyone on cached metrics without calling Shopify.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Configuration */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Configuration</CardTitle>
            {configMeta.updatedAt && (
              <p className="text-xs text-muted-foreground mt-1">Last saved {formatDate(configMeta.updatedAt)}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {dirty && (
              <Button variant="ghost" size="sm" onClick={() => setDraft(config)} disabled={saving}>
                Discard
              </Button>
            )}
            <Button
              className="bg-[#d7785b] hover:bg-[#c66a4f] text-white"
              disabled={!dirty || saving}
              onClick={handleSave}
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!draft ? (
            <div className="h-40 animate-pulse rounded-lg bg-muted" />
          ) : (
            <div className="space-y-8">
              <div className="flex items-center gap-3">
                <Switch
                  className="data-[state=checked]:bg-[#d7785b]"
                  checked={draft.enabled}
                  onCheckedChange={(value: boolean) => setField("enabled", value)}
                />
                <div>
                  <p className="text-sm font-medium text-foreground">Scheduled runs enabled</p>
                  <p className="text-xs text-muted-foreground">Off: the scheduled job records a no-op run. Manual runs still work.</p>
                </div>
              </div>

              <div>
                <p className="text-sm font-semibold text-foreground mb-3">Weights</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {weightFields.map(({ key, label }) => (
                    <div key={key}>
                      <label className="text-xs text-muted-foreground">{label}</label>
                      <Input
                        type="number"
                        min={0}
                        step={0.1}
                        value={String(draft[key])}
                        onChange={(e) => numberField(key, e.target.value)}
                        onWheel={(e) => (e.currentTarget as HTMLInputElement).blur()}
                      />
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">A weight of 0 removes that metric from the overall score.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground">Scoring mode</label>
                  <Select value={draft.scoringMode} onValueChange={(value) => setField("scoringMode", value as ScoringMode)}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {scoringModes.map((mode) => (
                        <SelectItem key={mode.value} value={mode.value}>
                          {mode.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    {scoringModes.find((m) => m.value === draft.scoringMode)?.hint}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Scoring window</label>
                  <Select value={draft.scoringWindow} onValueChange={(value) => setField("scoringWindow", value as ScoringWindow)}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {scoringWindows.map((window) => (
                        <SelectItem key={window.value} value={window.value}>
                          {window.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">Lifetime is the original behaviour; a rolling window stops dormant brands outranking growing ones.</p>
                </div>
              </div>

              <div>
                <p className="text-sm font-semibold text-foreground mb-3">Eligibility</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {toggleFields.map(({ key, label, hint }) => (
                    <div key={key} className="flex items-start gap-3">
                      <Switch
                        className="data-[state=checked]:bg-[#d7785b] mt-0.5"
                        checked={Boolean(draft[key])}
                        onCheckedChange={(value: boolean) => setField(key, value as never)}
                      />
                      <div>
                        <p className="text-sm text-foreground">{label}</p>
                        <p className="text-xs text-muted-foreground">{hint}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground">Minimum affiliates</label>
                  <Input type="number" min={0} value={String(draft.minAffiliates)} onChange={(e) => numberField("minAffiliates", e.target.value, true)} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Minimum affiliate orders</label>
                  <Input type="number" min={0} value={String(draft.minOrders)} onChange={(e) => numberField("minOrders", e.target.value, true)} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Revenue lookback (days)</label>
                  <Input type="number" min={0} max={365} value={String(draft.revenueLookbackDays)} onChange={(e) => numberField("revenueLookbackDays", e.target.value, true)} />
                  <p className="text-xs text-muted-foreground mt-1">Days re-pulled from Shopify each run so refunds and edits self-heal.</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Runs */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Card className="xl:col-span-2 gap-0 py-0 overflow-hidden">
          <CardHeader className="border-b border-border px-5 py-4 md:px-6">
            <CardTitle className="text-lg font-semibold text-foreground">Recent runs</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <DataTable
              columns={runColumns}
              data={runs}
              isLoading={loading}
              skeletonRows={5}
              getRowId={(row) => row.id}
              onRowClick={(row) => setSelectedRun(row)}
              rowClassName={(row) => (row.id === selectedRun?.id ? "bg-muted/50" : undefined)}
              emptyText="No runs yet"
              maxHeight="max-h-[32rem]"
            />
          </CardContent>
        </Card>

        <Card className="gap-0 py-0 overflow-hidden">
          <CardHeader className="border-b border-border px-5 py-4 md:px-6">
            <CardTitle className="text-lg font-semibold text-foreground">
              {selectedRun ? `Run ${formatDate(selectedRun.startedAt)}` : "Run details"}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 md:p-6 max-h-[32rem] overflow-y-auto">
            {!selectedRun ? (
              <p className="text-sm text-muted-foreground">Select a run to see what moved.</p>
            ) : (
              <div className="space-y-4">
                <RunSummary run={selectedRun} />
                {selectedRun.error && (
                  <p className="text-sm text-red-600 dark:text-red-400 break-words">{selectedRun.error}</p>
                )}
                {(selectedRun.warnings?.length ?? 0) > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Warnings</p>
                    <ul className="space-y-1">
                      {selectedRun.warnings!.map((warning, index) => (
                        <li key={index} className="text-xs text-amber-700 dark:text-amber-300 break-words">
                          {warning}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                    {selectedRun.dryRun ? "Would move" : "Moved"} ({selectedRun.changedCount})
                  </p>
                  {(selectedRun.changes?.length ?? 0) === 0 ? (
                    <p className="text-sm text-muted-foreground">Nothing.</p>
                  ) : (
                    <ul className="divide-y divide-border">
                      {selectedRun.changes!.map((change) => (
                        <li key={change.id} className="py-1.5 flex items-center justify-between gap-2 text-sm">
                          <span className="truncate text-foreground">
                            {change.companyName ?? change.id}
                            {change.pinned && <Pin className="inline w-3 h-3 ml-1 text-muted-foreground" aria-label="pinned" />}
                          </span>
                          <span className="shrink-0 text-muted-foreground">
                            #{change.from ?? "—"} → <span className="text-foreground font-medium">#{change.to}</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <ConfirmationModal
        isOpen={confirmRun}
        onClose={() => setConfirmRun(false)}
        onConfirm={handleRun}
        title="Run ranking now"
        message="This queues a full recompute, including the incremental Shopify revenue sync for every eligible brand. Only unpinned brands can move."
        confirmText="Queue run"
        isLoading={busy === "run"}
      />
    </div>
  );
}
